import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { getConfig } from '@/config';
import { type DpPriceReviewItem, type DpPriceRow } from '@/infrastructure/CompetitorV2Client';
import { StaffDpPriceApiError, StaffDpPriceClient } from '@/infrastructure/StaffDpPriceClient';
import {
  DP_PRICE_ERRORS,
  SOURCE_HOSTS,
  dpCurrentPriceText,
  dpProposedPrice,
  holdReasonLabel,
  safeProductUrl,
} from './DpPriceReviewText';

const money = (value: string | null, currency: string) => (value == null ? 'Unavailable' : `${value} ${currency}`);

export function DpStaffPriceReview({ row, onDone }: { row: DpPriceReviewItem; onDone: (result: DpPriceRow) => void }) {
  const { internalApiUrl, apiKey } = getConfig();
  const client = useMemo(() => new StaffDpPriceClient(internalApiUrl, apiKey), [internalApiUrl, apiKey]);
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const publish = dpProposedPrice(row);
  // A HELD row has no calculated price, so there is nothing to approve yet (how to handle it is still open).
  const approveBlocked = publish
    ? null
    : 'No new price was calculated, so there is nothing to approve. Reject it or wait for the next price run.';
  const ourUrl = safeProductUrl(row.productUrl, 'skin-seoul.com');
  const competitorUrl = safeProductUrl(row.competitorUrl, SOURCE_HOSTS[row.competitorSource ?? '']);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const submit = async (decision: 'APPROVED' | 'REJECTED') => {
    if (pending) {
      return;
    }
    setPending(true);
    setMessage('');
    setError('');
    try {
      // Without an amount the server publishes the proposed price into the slot (sale or regular) the product uses.
      const result = await client.act(row.targetSku, { decision });
      onDone(result);
      setMessage(
        result.status === 'PUBLISHED'
          ? `Published to the store: customers pay ${money(result.proposedSalePrice ?? result.proposedRegularPrice, 'SGD')}.`
          : result.status === 'REJECTED'
            ? 'Proposal rejected. The current store price is unchanged.'
            : `Saved with status ${result.status}.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['dp-price-reviews'] }).catch(() => undefined);
    } catch (caught) {
      if (!(caught instanceof StaffDpPriceApiError)) {
        setError('Result unknown — reload the queue to check the status.');
        return;
      }
      const { status, code } = caught;
      setError(
        code && DP_PRICE_ERRORS[code]
          ? DP_PRICE_ERRORS[code]
          : status === 401
            ? 'The Admin session could not be verified. Reload the page.'
            : status === 403
              ? 'The server did not authorize this price decision.'
              : `The price action failed${code ? `: ${code}` : '.'} The store price was not changed.`,
      );
    } finally {
      setPending(false);
    }
  };
  return (
    <Card>
      <Card.Header>Price review detail · {row.targetSku}</Card.Header>
      <Card.Body>
        <Table responsive bordered size="sm" aria-label="Price review summary" className="align-middle mb-3">
          <tbody>
            <tr>
              <th scope="row">Previous price</th>
              <td>{dpCurrentPriceText(row)}</td>
            </tr>
            <tr>
              <th scope="row">New price</th>
              <td>
                {publish ? `${publish} SGD` : 'No new price'}
                {!row.proposedSalePrice && Number(row.proposedRegularPrice) > Number(row.previousEffectivePrice) && (
                  <div className="small text-muted">
                    Regular price rises to {money(row.proposedRegularPrice, 'SGD')}; the sale price is removed
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">Why review</th>
              <td>{row.holdReason ? holdReasonLabel(row.holdReason) : 'None'}</td>
            </tr>
          </tbody>
        </Table>
        <div className="d-flex flex-wrap gap-2">
          <Button disabled={pending || approveBlocked !== null} onClick={() => void submit('APPROVED')}>
            Approve
          </Button>
          <Button variant="outline-danger" disabled={pending} onClick={() => void submit('REJECTED')}>
            Reject
          </Button>
          {ourUrl && (
            <a className="btn btn-outline-secondary" href={ourUrl} target="_blank" rel="noopener noreferrer">
              Our product
            </a>
          )}
          {competitorUrl && (
            <a className="btn btn-outline-secondary" href={competitorUrl} target="_blank" rel="noopener noreferrer">
              Competitor product
            </a>
          )}
        </div>
        {approveBlocked && <Form.Text className="d-block">{approveBlocked}</Form.Text>}
        {error && (
          <Alert variant="danger" role="alert" className="mt-3">
            {error}
          </Alert>
        )}
        {message && (
          <Alert variant="success" role="status" className="mt-3">
            {message}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}
